/**
 * Hook to check whether a gated settings section is accessible.
 *
 * Evaluates feature-flag and permission gates defined on settings nav items.
 * Used by both the desktop settings sidebar (SettingsSideNavigation) and the
 * settings layout (redirect logic + mobile list).
 *
 * Returns:
 * - isGateAllowed: check function for a single gate config
 * - isFlagsLoading: true while feature flags are still being fetched
 *
 * CRITICAL: Callers MUST NOT redirect while isFlagsLoading is true, or
 * users refreshing at a flag-gated URL will be permanently bounced to
 * the default section before flags finish loading.
 */

import { useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { usePermissions } from '@/hooks/usePermissions';

import {
  BILLING_ADMIN_PERMISSION,
  CRM_ADMIN_PERMISSION,
  TEAMS_ADMIN_PERMISSION,
} from './settings-nav-config';

export interface SettingsGate {
  featureFlag?: string;
  permission?: string;
  /**
   * Gate on read access to a specific CRM entity type (e.g.
   * 'ahoy_validation_rule'). Unlike `permission` (a permission *group*), this
   * checks per-entity read access — so a member with read-only access to the
   * entity can see the section while write actions stay separately gated
   * inside it.
   */
  readEntityType?: string;
  /**
   * INVERSE of `featureFlag`: hide this section from the settings nav while the
   * named flag is ACTIVE. For a section that has moved somewhere else, where
   * the flag is what makes the new home exist.
   *
   * Deliberately not part of the allow/deny evaluation in `isGateAllowed` —
   * that result also drives the access redirect in `settings-layout.tsx`, and
   * a section that has moved needs to redirect to its NEW home, not to the
   * default section. Visibility and access are separate questions here; this
   * one answers only "show it in the nav?".
   */
  hiddenWhenFlag?: string;
}

export function useSettingsGate() {
  const { currentOrg } = useOrg();
  const { data: featureFlags, isLoading } = useFeatureFlags(currentOrg?.id);
  const { hasPermission, hasPermissionGroup } = usePermissions();
  const canManageBilling = hasPermissionGroup(BILLING_ADMIN_PERMISSION);
  const canManageCrm = hasPermissionGroup(CRM_ADMIN_PERMISSION);
  const canManageTeams = hasPermissionGroup(TEAMS_ADMIN_PERMISSION);

  const flagMap: Record<string, boolean> = useMemo(
    () => ({
      mcp_oauth: featureFlags?.includes('mcp_oauth') ?? false,
      api_keys: featureFlags?.includes('api_keys') ?? false,
      recall_notetaker: featureFlags?.includes('recall_notetaker') ?? false,
      ahoy_desktop_app: featureFlags?.includes('ahoy_desktop_app') ?? false,
      slack_sync: featureFlags?.includes('slack_sync') ?? false,
      activecampaign_sync:
        featureFlags?.includes('activecampaign_sync') ?? false,
      stripe_sync: featureFlags?.includes('stripe_sync') ?? false,
      whatsapp_sync: featureFlags?.includes('whatsapp_sync') ?? false,
      mailchimp_sync: featureFlags?.includes('mailchimp_sync') ?? false,
      hubspot_sync: featureFlags?.includes('hubspot_sync') ?? false,
      salesforce_sync: featureFlags?.includes('salesforce_sync') ?? false,
      quo_sync: featureFlags?.includes('quo_sync') ?? false,
      inbound_webhooks: featureFlags?.includes('inbound_webhooks') ?? false,
      form_collection: featureFlags?.includes('form_collection') ?? false,
      team_management: featureFlags?.includes('team_management') ?? false,
      validation_rules: featureFlags?.includes('validation_rules') ?? false,
      // Load-bearing entry, not a convenience: this map is the ONLY source
      // `isGateAllowed`/`isGateVisible` consult, and an unlisted flag resolves
      // to false — i.e. permanently off — so a gate naming a missing flag
      // fails silently rather than loudly.
      builder_mode: featureFlags?.includes('builder_mode') ?? false,
      service_account: featureFlags?.includes('service_account') ?? false,
      // Gates AI → Agent Autonomy (settings-nav-config.ts).
      auto_approve_creation:
        featureFlags?.includes('auto_approve_creation') ?? false,
    }),
    [featureFlags]
  );

  const permissionMap: Record<string, boolean> = useMemo(
    () => ({
      [BILLING_ADMIN_PERMISSION]: canManageBilling,
      [CRM_ADMIN_PERMISSION]: canManageCrm,
      [TEAMS_ADMIN_PERMISSION]: canManageTeams,
    }),
    [canManageBilling, canManageCrm, canManageTeams]
  );

  const isGateAllowed = useCallback(
    (gate: SettingsGate | undefined): boolean => {
      if (!gate) {
        return true;
      }
      if (gate.featureFlag && !flagMap[gate.featureFlag]) {
        return false;
      }
      if (gate.permission && !permissionMap[gate.permission]) {
        return false;
      }
      if (gate.readEntityType && !hasPermission(gate.readEntityType)) {
        return false;
      }
      return true;
    },
    [flagMap, permissionMap, hasPermission]
  );

  /**
   * Whether a section should appear in the settings nav.
   *
   * Separate from `isGateAllowed` on purpose — see `hiddenWhenFlag`. A hidden
   * section is still resolvable by URL, which is what lets the section's new
   * home redirect rather than 404.
   */
  const isGateVisible = useCallback(
    (gate: SettingsGate | undefined): boolean => {
      if (!isGateAllowed(gate)) {
        return false;
      }
      if (gate?.hiddenWhenFlag && flagMap[gate.hiddenWhenFlag]) {
        return false;
      }
      return true;
    },
    [isGateAllowed, flagMap]
  );

  return {
    isGateAllowed,
    isGateVisible,
    isFlagsLoading: isLoading,
  };
}
